<script setup lang="ts">
import { useHead } from "@unhead/vue"
import { computed } from "vue"
import { useRouter } from "vue-router"
import { useGetEventVault } from "@/api"
import { DeleteEventModal, EventSettingsForm } from "@/components"
import { Button, Card } from "@/components/ui"
import { useToast } from "@/components/ui/ToastReka/useToast"
import { useConvertedRouteParam, useModal, useTranslation } from "@/composables"
import { SidebarHostEventLayout } from "@/layouts"
import { EventVaultStatus } from "@/models"
import { DeleteEventModalKey } from "@/symbols"

definePage({
  meta: {
    requiresAuth: true,
  },
})

const { t } = useTranslation()
const toast = useToast()
const router = useRouter()

useHead({ title: t("event_settings_page.title") })

const eventVaultId = useConvertedRouteParam<number>("id", "/event/[id]/advanced-settings", value => Number(value))

const { data: eventVault } = useGetEventVault(eventVaultId, { include: ["zip"] })

const { toggle: toggleConfirmDelete } = useModal(DeleteEventModalKey)

function onSettingUpdateSuccess() {
  toast.success({ title: t("success"), description: t("event_settings_page.success_message") })
}

function onDeleteEventSuccess() {
  router.push("/event/dashboard")
}

function onSettingUpdateError() {
  toast.error()
}

const canDeleteEvent = computed(() => {
  const status = eventVault.value?.status

  if (!status)
    return false

  return [
    EventVaultStatus.Draft,
    EventVaultStatus.Ready,
    EventVaultStatus.Live,
    EventVaultStatus.Closed,
    EventVaultStatus.Expired,
    EventVaultStatus.Expiring,
  ].includes(status)
})
</script>

<template>
  <SidebarHostEventLayout v-slot="{ toggleExportDrawer }">
    <h1 class="font-heading font-bold text-2xl mb-8">
      {{ t("event_settings_page.title") }}
    </h1>
    <EventSettingsForm
      @success="onSettingUpdateSuccess"
      @error="onSettingUpdateError"
    />

    <Card v-if="canDeleteEvent" variant="danger" class="mt-16">
      <div class="flex flex-col gap-4">
        <div>
          <h3 class="text-xl font-heading font-bold text-theme-danger">
            {{ t("event_settings_page.delete_title") }}
          </h3>
        </div>
        <p class="text-sm md:text-base text-label">
          <i18n-t keypath="event_settings_page.delete_message" scope="global">
            <template #zipGenerated>
              <button class="text-link underline cursor-pointer" @click.prevent="toggleExportDrawer()">
                {{ t("event_settings_page.delete_message_link") }}
              </button>
            </template>
          </i18n-t>
        </p>
        <div>
          <Button variant="danger" size="sm" icon="Delete" class="w-full md:w-auto" data-test="delete-event" @click.prevent="toggleConfirmDelete">
            {{ t("event_settings_page.delete_button") }}
          </Button>
        </div>
      </div>
    </Card>

    <DeleteEventModal :event-vault="eventVault" @success="onDeleteEventSuccess" />
  </SidebarHostEventLayout>
</template>
